import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { ContactPeek, ContactViews, ContactsTable, contactName } from '../../components/contacts-ledger';
import { LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import {
  ContactListParams, ContactTypeFilter, useContactDetail, useContactsPage, useDeleteContact,
} from '../../hooks/useContactQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import contactService from '../../services/contact.service';
import { Contact, ContactFilters } from '../../types/contact.types';
import { cn } from '../../lib/cn';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [type, setType] = useState<ContactTypeFilter>('ALL');
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const toast = useToast();

  // Show feedback handed off by ContactForm through navigation state
  useEffect(() => {
    const handoff = (
      location.state as { snackbar?: { message: string; severity: 'success' | 'error' | 'info' | 'warning' } } | null
    )?.snackbar;
    if (handoff) {
      // Fall back to info when a tampered/stale history.state carries a
      // severity outside the known set, rather than crashing on an undefined
      // method lookup.
      (toast[handoff.severity] ?? toast.info)(handoff.message);
      // Clear the state so a refresh or back-navigation doesn't replay it
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const params: ContactListParams = useMemo(
    () => ({ search, type, activeOnly, page }),
    [search, type, activeOnly, page],
  );
  const { data, isPending, isError } = useContactsPage(params);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;

  useEffect(() => {
    if (isError) toast.error('Failed to load contacts');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Tailwind's lg breakpoint; the peek is a right rail on desktop and a bottom
  // sheet on mobile, and the sheet portals to <body>, so CSS can't gate it.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const selectedContact = rows.find((contact) => contact.id === selectedId) ?? null;
  // A selected contact can leave the current page (filter or page change); fetch
  // it by id so the peek survives.
  const peekFallback = useContactDetail(peekOpen && selectedContact == null ? selectedId : null);
  const peekContact = selectedContact ?? peekFallback.data ?? null;

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((contact) => contact.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  useLedgerListKeys({
    enabled: deleting == null,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const changeType = (value: ContactTypeFilter) => {
    setType(value);
    setPage(1);
  };
  const changeActiveOnly = (value: boolean) => {
    setActiveOnly(value);
    setPage(1);
  };
  const resetView = () => {
    setSearch('');
    setType('ALL');
    setActiveOnly(false);
    setPage(1);
  };

  const openEdit = (id: string) => navigate(`/contacts/${id}/edit`);

  // Unlike the risk register's deliberately-full export, the contacts export
  // honors the active view filters, as the pre-ledger contacts screen did.
  const [exporting, setExporting] = useState(false);
  const exportContacts = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const filters: ContactFilters = {};
      if (search) filters.search = search;
      if (type !== 'ALL') filters.contact_type = [type];
      if (activeOnly) filters.is_active = true;
      const blob = await contactService.exportContacts(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Contacts exported successfully');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const deleteContact = useDeleteContact();
  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    // Close synchronously so a second click can't queue a duplicate delete.
    setDeleting(null);
    deleteContact.mutate(target.id, {
      onSuccess: () => {
        // Contacts are server-paged and an out-of-range page 404s, so deleting
        // the only row of a trailing page must step back before the
        // invalidation refetch targets a page that no longer exists.
        if (rows.length === 1 && page > 1) setPage(page - 1);
        toast.success('Contact deleted');
        setPeekOpen(false);
        setSelectedId(null);
      },
      onError: () => {
        toast.error('Delete failed');
      },
    });
  };

  return (
    <PageWrapper
      title="Contacts"
      subtitle={isPending ? undefined : `${count} contact${count === 1 ? '' : 's'}`}
      noPaper
      maxWidth="xl"
      actions={
        <span className="flex items-baseline gap-3">
          <button
            type="button"
            onClick={exportContacts}
            disabled={exporting}
            className="cap-label cursor-pointer hover:!text-ink disabled:cursor-default disabled:opacity-60"
          >
            Export
          </button>
          <button
            type="button"
            onClick={() => navigate('/contacts/new')}
            className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
          >
            + Add contact
          </button>
        </span>
      }
    >
      <div className="hidden md:block">
        <ContactViews
          search={search}
          type={type}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onTypeChange={changeType}
          onActiveOnlyChange={changeActiveOnly}
        />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <ContactViews
          search={search}
          type={type}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onTypeChange={(next) => {
            changeType(next);
            setSheetOpen(false);
          }}
          onActiveOnlyChange={changeActiveOnly}
        />
      </LedgerSheet>

      <div className={cn('mt-1', peekOpen && peekContact && isDesktop && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          <ContactsTable
            rows={rows}
            count={count}
            page={page}
            isPending={isPending}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onPageChange={setPage}
            onResetView={resetView}
          />
        </div>
        {peekOpen && peekContact && isDesktop && (
          <ContactPeek contact={peekContact} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
        )}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekContact != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="Contact"
      >
        {peekContact && (
          <ContactPeek contact={peekContact} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
        )}
      </LedgerSheet>

      <ConfirmDialog
        open={deleting != null}
        title="Delete contact?"
        message={`This permanently removes ${deleting ? contactName(deleting) : ''} from the register.`}
        confirmText="Delete"
        severity="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageWrapper>
  );
};

export default ContactsPage;
